import * as AlertDialogPrimitive from "@radix-ui/react-dialog";
import React from "react";

import { cn } from "../cn";
import { buttonVariants } from "./button";

export const AlertDialog = AlertDialogPrimitive.Root;
export const AlertDialogTrigger = AlertDialogPrimitive.Trigger;
export const AlertDialogPortal = AlertDialogPrimitive.Portal;
export const AlertDialogClose = AlertDialogPrimitive.Close;

export const AlertDialogOverlay = React.forwardRef<React.ElementRef<typeof AlertDialogPrimitive.Overlay>, React.ComponentProps<typeof AlertDialogPrimitive.Overlay>>(function AlertDialogOverlay(
  { className, ...props },
  ref,
) {
  return (
    <AlertDialogPrimitive.Overlay
      ref={ref}
      data-slot="alert-dialog-overlay"
      className={cn(
        "tw-fixed tw-inset-0 tw-z-50 tw-bg-backdrop",
        "data-[state=open]:tw-animate-in data-[state=open]:tw-fade-in-0",
        "data-[state=closed]:tw-animate-out data-[state=closed]:tw-fade-out-0",
        className,
      )}
      {...props}
    />
  );
});

export function AlertDialogContent({ className, ...props }: React.ComponentProps<typeof AlertDialogPrimitive.Content>) {
  return (
    <AlertDialogPortal>
      <AlertDialogOverlay />
      <AlertDialogPrimitive.Content
        data-slot="alert-dialog-content"
        role="alertdialog"
        // Confirmation dialogs close only through an explicit action.
        onPointerDownOutside={(event) => event.preventDefault()}
        onInteractOutside={(event) => event.preventDefault()}
        onEscapeKeyDown={(event) => event.preventDefault()}
        className={cn(
          "tw-fixed tw-left-1/2 tw-top-1/2 tw-z-50 -tw-translate-x-1/2 -tw-translate-y-1/2",
          "tw-flex tw-w-[calc(100%-2rem)] tw-max-w-sm tw-flex-col tw-gap-4 tw-rounded-xl tw-border tw-border-solid tw-border-border tw-bg-background tw-p-8 tw-shadow-lg",
          "data-[state=open]:tw-animate-in data-[state=open]:tw-fade-in-0 data-[state=open]:tw-zoom-in-95",
          "data-[state=closed]:tw-animate-out data-[state=closed]:tw-fade-out-0 data-[state=closed]:tw-zoom-out-95",
          className,
        )}
        {...props}
      />
    </AlertDialogPortal>
  );
}

export function AlertDialogHeader({ className, ...props }: React.ComponentProps<"div">) {
  return <div data-slot="alert-dialog-header" className={cn("tw-flex tw-flex-col tw-gap-4 tw-text-center", className)} {...props} />;
}

export function AlertDialogFooter({ className, ...props }: React.ComponentProps<"div">) {
  return <div data-slot="alert-dialog-footer" className={cn("tw-flex tw-flex-col tw-gap-2", className)} {...props} />;
}

export function AlertDialogTitle({ className, ...props }: React.ComponentProps<typeof AlertDialogPrimitive.Title>) {
  // Preflight is disabled, so reset the native heading margin.
  return <AlertDialogPrimitive.Title data-slot="alert-dialog-title" className={cn("tw-m-0 tw-text-xl tw-font-medium tw-text-foreground", className)} {...props} />;
}

export function AlertDialogDescription({ className, ...props }: React.ComponentProps<typeof AlertDialogPrimitive.Description>) {
  // Preflight is disabled, so reset the native paragraph margin.
  return <AlertDialogPrimitive.Description data-slot="alert-dialog-description" className={cn("tw-m-0 tw-text-sm tw-text-muted-foreground", className)} {...props} />;
}

export function AlertDialogAction({ className, ...props }: React.ComponentProps<"button">) {
  return <button type="button" data-slot="alert-dialog-action" className={cn(buttonVariants(), "tw-w-full", className)} {...props} />;
}

export function AlertDialogCancel({ className, ...props }: React.ComponentProps<"button">) {
  return (
    <AlertDialogPrimitive.Close asChild>
      <button type="button" data-slot="alert-dialog-cancel" className={cn(buttonVariants({ variant: "outline" }), "tw-w-full", className)} {...props} />
    </AlertDialogPrimitive.Close>
  );
}
